import React, { useState } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import get from "lodash/get";

import { ReactBoosts } from "features/boosts";
import NavBarUI from "shared/components/NavBar/NavBar";
import Context from "shared/util/Context";
import getPlaceholderImageUrl from "shared/util/getPlaceholderImageUrl";
import useBoostController from "shared/hooks/useBoostController";
import isRouteMatch from "shared/util/isRouteMatch";
import useBodyClassName from "routers/shared/hooks/useBodyClassName";
import { ROUTES, ROUTE_LIST } from "routers/shared/util/consts";

// Routes to hide the navbar on.
export const NO_NAVBAR_ROUTES = [
	...ROUTES.AccountReactivate,
	...ROUTES.Logout,
	...ROUTES.Offboarding,
	...ROUTES.Onboarding,
	...ROUTES.PhoneWall,
	...ROUTES.Signup,
	...ROUTES.Splash,
	...ROUTES.StandaloneConversation,
];

const NAVBAR_INFO_QUERY = gql`
	query desktopNavBarInfo($userid: String!) {
		user(id: $userid) {
			id
			displayname
			primaryImage {
				square225
			}
			isIncognito
			notificationCounts {
				likesMutual
				likesIncoming
				messages
			}
		}
	}
`;

const NavBar = ({ userid, location }) => {
	// General NavBar stuff.
	const isLoggedIn = !!userid;
	const is404 = !isRouteMatch(location, ROUTE_LIST);
	const showNavBar = !is404 && !isRouteMatch(location, NO_NAVBAR_ROUTES);
	const { data, loading } = useQuery(NAVBAR_INFO_QUERY, {
		variables: { userid },
		skip: !isLoggedIn,
	});
	const isFlagMod = !!Context.get("isFlagMod");
	const isAlist = !!Context.get("isAlist");
	const isStaff = !!Context.get("isStaff");
	const displayName = get(data, "user.displayname", "");
	const thumbnail = get(data, "user.primaryImage.square225", getPlaceholderImageUrl());
	const totalLikesCount = get(data, "user.notificationCounts.likesIncoming", 0) + get(data, "user.notificationCounts.likesMutual", 0);
	const likesCount = totalLikesCount > 99 ? "99+" : totalLikesCount;
	const messagesCount = get(data, "user.notificationCounts.messages", 0);
	const hasIncognitoEnabled = get(data, "user.isIncognito", false);

	// Boost-related stuff.
	const [isFinishedBoostModalOpen, setIsFinishedBoostModalOpen] = useState(false);
	const closeFinishedBoostModal = () => setIsFinishedBoostModalOpen(false);
	const {
		startBoost: _startBoost,
		endBoost,
		activeBoost,
		boostTokenCount,
	} = useBoostController({
		userid,
		onBoostEnd: () => setIsFinishedBoostModalOpen(true),
	});
	const boostStartTime = activeBoost && activeBoost.startTime;
	const boostEndTime = activeBoost && activeBoost.endTime;
	const attentionMultiplier = activeBoost && activeBoost.attentionMultiplier;
	const startBoost = ({ boostAgainCTA } = {}) => {
		if (!boostTokenCount) {
			ReactBoosts.open({ cameFrom: boostAgainCTA ? "boostagain" : "desktopnavbar" });
		} else {
			_startBoost();
		}
	};

	useBodyClassName(showNavBar ? "has_navbar" : "no_navbar");

	if (!showNavBar) {
		return null;
	}

	return (
		<nav id="navigation">
			<NavBarUI
				isLoading={loading}
				isLoggedIn={isLoggedIn}
				displayName={displayName}
				thumbnail={thumbnail}
				isStaff={isStaff}
				isAlist={isAlist}
				isFlagMod={isFlagMod}
				isBoosting={!!activeBoost}
				boostStartTime={boostStartTime}
				boostEndTime={boostEndTime}
				multiplier={attentionMultiplier}
				boostTokenCount={boostTokenCount}
				likesCount={likesCount}
				messagesCount={messagesCount}
				hasIncognitoEnabled={hasIncognitoEnabled}
				isFinishedBoostModalOpen={isFinishedBoostModalOpen}
				closeFinishedBoostModal={closeFinishedBoostModal}
				startBoost={startBoost}
				endBoost={endBoost}
				location={location}
			/>
		</nav>
	);
};

NavBar.propTypes = {
	userid: PropTypes.string,
	location: PropTypes.object.isRequired,
};

NavBar.defaultProps = {
	userid: null,
};

export default NavBar;



// WEBPACK FOOTER //
// ./src/routers/Desktop/NavBar/index.jsx